// Stored as workflows: editorData = doc, description = "When to use".

import type { JSONContent } from '@tiptap/react';
import { BaseAdapter } from './base.adapter';
import type { Workflow, WorkflowSummary } from '@/types';
import type { SEGDocument } from '@/modules/workflows/components/seg-viewer';
import { ensureSections, sanitizeProtocolDoc, syncSubProtocolLabels } from '@/modules/protocols/utils/protocol-sections';
import type { Protocol, ProtocolSummary } from '@/modules/protocols/types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// The stored doc made safe to open: unknown nodes dropped, both sections guaranteed.
function toDoc(value: unknown): JSONContent {
  const raw = isRecord(value) && value['type'] === 'doc' ? (value as JSONContent) : undefined;
  return syncSubProtocolLabels(ensureSections(raw ? (sanitizeProtocolDoc(raw) ?? undefined) : undefined));
}

export class ProtocolAdapter extends BaseAdapter {
  static fromWorkflow(workflow: Workflow): Protocol {
    return {
      id: workflow.id,
      name: workflow.name,
      description: workflow.description ?? '',
      live: workflow.status,
      enabledChannels: workflow.enabledChannels ?? [],
      doc: toDoc(workflow.editorData),
      compilationStatus: workflow.compilationStatus,
      seg: (workflow.seg as SEGDocument | null | undefined) ?? null,
      compiledAt: workflow.segCompiledAt ?? null,
      editedAt: workflow.editorDataUpdatedAt ?? null,
      category: workflow.category,
      subcategory: workflow.subcategory,
      sopId: workflow.sopId ?? null,
      ticketCount: workflow.ticketIds?.length ?? 0
    };
  }

  static fromSummary(summary: WorkflowSummary): ProtocolSummary {
    return {
      id: summary.id,
      name: summary.name,
      description: summary.description ?? '',
      live: summary.status,
      enabledChannels: summary.enabledChannels ?? [],
      updatedAt: summary.updatedAt,
      category: summary.category,
      subcategory: summary.subcategory,
      categorizeMiss: summary.categorizeMiss,
      vectorSyncStatus: summary.vectorSyncStatus,
      compilationStatus: summary.compilationStatus,
      compilationError: summary.compilationError
    };
  }

  static fromSummaryList(summaries: WorkflowSummary[]): ProtocolSummary[] {
    return summaries.map((summary) => this.fromSummary(summary));
  }

  // The document goes into editorData as-is; that is what the compiler validates and reads.
  static toEditorData(protocol: Protocol): Record<string, unknown> {
    return (sanitizeProtocolDoc(protocol.doc) ?? ensureSections(undefined)) as Record<string, unknown>;
  }
}
